import * as React from 'react';
import PriceChartEventMarker from '@components/PriceChart/components/PriceChartEventMarker/PriceChartEventMarker';
import { PriceChartEventLabel } from '@components/PriceChart/components/PriceChartEventLabel';
import type { ScaleTime, ScaleLinear } from 'd3-scale';
import If from '@components/If';
import type { Category } from '@/components/PriceChart/query';
import loadable from '@loadable/component';
import { useRegistrationModal } from '@/context/RegistrationModalContext';

const Events = loadable(() => import('@/components/Events'), {
  resolveComponent: (component) => component.Events,
});

const MemoPriceChartEventLabel = React.memo(PriceChartEventLabel);
const MemoPriceChartEventMarker = React.memo(PriceChartEventMarker);
interface Props {
  dx: number;
  top: number;
  height: number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  onClick: (data: { id: string; date: number }) => void;
  containerWidth: number;
  eventHistory?: {
    date: number;
    events: {
      id: string;
      category: Category;
      name: string;
    }[];
  }[];
}

type LabelMap = Record<string, { visible: boolean; width: number }>;

export function PriceChartEvent({
  dx,
  top,
  eventHistory,
  height,
  xScale,
  containerWidth,
  onClick,
}: Props) {
  const [render, reRender] = React.useState({});
  const [selectedEvents, setSelectedEvents] = React.useState('');
  const labelMapRef = React.useRef<LabelMap>({});
  const { shouldShowRego, showRegoModal } = useRegistrationModal();

  const handleClick = React.useCallback(
    (data: { id: string; date: number }) => {
      const selected = eventHistory?.find((event) => event.date === data.date);
      if (typeof selected === 'undefined') return;
      if (shouldShowRego) {
        showRegoModal();
        return;
      }
      const concatId = selected.events.map((n) => n.id).join(',');
      setSelectedEvents(concatId);
      if (typeof onClick === 'function') {
        onClick(data);
      }
    },
    [eventHistory, shouldShowRego, onClick, showRegoModal]
  );

  const handleLabelUpdate = React.useCallback((date: number, width: number) => {
    labelMapRef.current[date.toString()] = { visible: false, width };
  }, []);

  const visibleLabels = React.useMemo(() => {
    if (typeof eventHistory === 'undefined') return [];
    let delta = 999999;
    return eventHistory
      .filter((event) => {
        const { date } = event;
        //	Weird. Needs to change
        if (date in labelMapRef.current) {
          const width = labelMapRef.current[date.toString()].width;
          const x = xScale && xScale(date);
          if (x && x < delta) {
            delta = x - (width + 16);
            return true;
          } else {
            return false;
          }
        }
        return false;
      })
      .map((event) => event.date);
  }, [eventHistory, xScale, dx, render]);
  // Hides offscreen dates
  const filteredEvents = React.useMemo(() => {
    if (typeof eventHistory === 'undefined') return [];
    return eventHistory.filter((event) => {
      const { date } = event;
      const xPos = xScale && xScale(date);
      if (
        typeof xPos !== 'undefined' &&
        xPos !== null &&
        xPos > -dx &&
        xPos < -dx + containerWidth
      ) {
        return true;
      }
      return false;
    });
  }, [eventHistory, dx, containerWidth, xScale]);

  React.useLayoutEffect(() => {
    /** we want a rerender here once all 'handleLabelUpdate' is done */
    reRender({});
  }, []);

  if (typeof eventHistory === 'undefined') return null;
  return (
    <>
      <g transform={`translate(0, ${top})`}>
        {filteredEvents.map((event) => {
          const { date, events } = event;
          const xpos = xScale(date);
          /** only 1 event for the label we'll take the first one */
          const [eventLabel] = events;
          const concatId = events.map((n) => n.id).join(',');
          return (
            <g key={`${date}-${concatId}`} transform={`translate(${xpos}, 0)`}>
              <MemoPriceChartEventLabel
                y={height + 20}
                id={concatId}
                date={date}
                category={eventLabel.category}
                shortName={eventLabel.name}
                visibility={visibleLabels.includes(date) ? 'visible' : 'hidden'}
                onLabelUpdate={handleLabelUpdate}
                onClick={handleClick}
              />
              <MemoPriceChartEventMarker
                events={events}
                y={height - 10}
                date={date}
                onClick={handleClick}
              />
            </g>
          );
        })}
      </g>
      <If match={selectedEvents !== ''}>
        {() => (
          <Events
            eventsId={selectedEvents}
            onClose={() => {
              setSelectedEvents('');
            }}
          />
        )}
      </If>
    </>
  );
}

interface PriceChartEventLinesProps {
  top: number;
  dx: number;
  height: number;
  containerWidth: number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  data?: { date: number; close: number | null }[];
}

export const PriceChartEventLines = ({
  top,
  data,
  xScale,
  yScale,
  height,
  dx,
  containerWidth,
}: PriceChartEventLinesProps) => {
  if (typeof data === 'undefined') {
    return null;
  }
  return (
    <g transform={`translate(0, ${top})`}>
      {data
        .filter(({ date }) => {
          const xPos = xScale && xScale(date);
          if (
            typeof xPos !== 'undefined' &&
            xPos !== null &&
            xPos > -dx &&
            xPos < -dx + containerWidth
          ) {
            return true;
          }
          return false;
        })
        .map(({ date, close }, i) => {
          return (
            <g
              key={`${date.toString()}-${i}`}
              transform={`translate(${xScale && xScale(date)}, 0)`}
            >
              {close !== null && (
                <line
                  x1="0"
                  y1={yScale && yScale(close)}
                  x2="0"
                  y2={height - 10}
                  strokeWidth="1"
                  stroke="#1F2A35"
                />
              )}
            </g>
          );
        })}
    </g>
  );
};

export default PriceChartEvent;
